import * as React from 'react';

export type FormInputSymbolProps = React.DetailedHTMLProps<React.InputHTMLAttributes<HTMLInputElement>, HTMLInputElement> & {
	symbol: string;
	position?: 'before' | 'after' | 'hidden';
	prominent?: boolean;
};

const permittedSymbols = '0123456789-.,';

const softValidate = (input: string) => Array.from(input)
	.map((char) => permittedSymbols.includes(char))
	.reduce((prev, current) => prev && current, true);

const hardValidate = (input: string) => input === '' || input.match(/(^-*\d*(\.\d*)*)$/gm);

export const FormInputSymbol: React.FC<FormInputSymbolProps> = (props) => {
	const attrs = props || {} as FormInputSymbolProps;

	const [editing, setEditing] = React.useState(false);
	const {value, ...newAttrs} = attrs;
	const [internalValue, setInternalValue] = React.useState(`${value}`);
	if (!editing && `${value}` !== internalValue) {
		setInternalValue(`${value}`);
	}
	const valid = hardValidate(internalValue);
	const position = !attrs.position ? 'before' : attrs.position;

	const classList = [
		'form__input',
		`form__input--${position}`,
		...(attrs['is-valid'] === 'false' || false ? ['form__input--error'] : []),
		...(attrs.readOnly ? ['form__input--read-only'] : []),
		...(attrs.disabled ? ['form__input--disabled'] : []),
		...(attrs.prominent ? ['form__input--prominent'] : []),
		...(!valid && !(attrs.readOnly || attrs.disabled) ? ['form__input--error'] : [])
	];

	const sidecarClassList = [
		'form__input--symbol-sidecar',
		`form__input--symbol-sidecar-${position}`,
		...(attrs.prominent && valid ? ['form__input--prominent'] : []),
		...(!valid && !(attrs.readOnly || attrs.disabled) ? ['form__input--error'] : []),
		...((attrs['is-valid'] === 'false' || attrs['is-valid'] === false) ? ['form__input--error'] : []),
	];

	const sidecar = <div className={sidecarClassList.join(' ')}>
		<div className="form__input--symbol">{attrs.symbol}</div>
	</div>;

	return <div className={`form__input--container-symbol form__input--container container`}>
		{position === 'before' && sidecar}
		<input className={classList.join(' ')}
			{...newAttrs}
			type="text"
			inputMode="decimal"
			value={internalValue}
			onChange={(e) => {
				const val = e.target.value;
				if (softValidate(val)) {
					setInternalValue(e.target.value);
				} else {
					setInternalValue(internalValue);
				}
			}}
			onFocus={(e) => {
				if (internalValue === '0') {
					setInternalValue('');
				}

				setEditing(true);
			}}
			onBlur={(e) => {
				const val = e.target.value === '' ? '0' : e.target.value;
				setInternalValue(val);

				if (attrs.onChange && valid) {
					const changeEvent = {
						target: { value: val, valueAsNumber: Number(val) }
					} as React.ChangeEvent<HTMLInputElement>;

					attrs.onChange(changeEvent);
				}

				setEditing(false);
			}}
			onKeyPress={(e) => {
				// Handle enter
				if (e.key === 'Enter') {
					const val = internalValue === '' ? '0' : internalValue;

					if (attrs.onChange && valid) {
						const changeEvent = {
							target: { value: val, valueAsNumber: Number(val) }
						} as React.ChangeEvent<HTMLInputElement>;

						attrs.onChange(changeEvent);
					}
				}
			}}
		/>
		{position === 'after' && sidecar}
	</div>;
};
